import { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, betaAddGmailLabel } from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { LabelModel } from '../../../../../../types';
import SharedDialog from '../../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { Messages } from '../../../../../utils/message-constants';
import { UseFormSetValue } from 'react-hook-form';
import { FilterForm } from '../filters/add-gmail-filter-dialog';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface AddGmailLabelProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  userLabels: LabelModel[];
  changeLogRoles: string[];
  setSelectedLabelId?: (id: string) => void;
  setValue?: UseFormSetValue<FilterForm>;
}

export default function AddGmailLabelDialog({
  open,
  handleClose,
  userEmail,
  userLabels,
  changeLogRoles,
  setSelectedLabelId,
  setValue,
}: AddGmailLabelProps) {
  const { createEvent } = useAnalytics();
  const [labelName, setLabelName] = useState('');
  const [labelVisibility, setlabelVisibilty] =
    useState<LabelModel['labelVisibility']>('labelShow');
  const [messageVisibility, setMessageVisibility] =
    useState<LabelModel['messageVisibility']>('show');
  const [hasError, setHasError] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () => betaAddGmailLabel(userEmail, newLabel, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Label'),
      });

      const newList = [
        ...userLabels,
        {
          ...data,
          type: 'user',
        },
      ];

      queryClient.setQueryData([UserKeys.GmailLabels, userEmail], newList);
      if (setSelectedLabelId) {
        setSelectedLabelId(data.id);
      }

      if (setValue) {
        setValue('applyLabel', data.id, {
          shouldDirty: true,
        });
      }

      setLabelName('');
      setlabelVisibilty('labelShow');
      setMessageVisibility('show');
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Creating('the label'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .create_gmail_label,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.labels,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (allLabelNames.includes(labelName.toLocaleLowerCase())) {
      setHasError(true);
    } else {
      setHasError(false);
      if (isAbleToSeeUIElement(changeLogRoles, UserRoles.AddGmailLabel.value)) {
        setOpenChangeLogDialog(true);
      } else {
        mutate();
      }
    }
  }

  const labelVisibiltyOptions = [
    { value: 'labelShow', displayValue: 'Show' },
    { value: 'labelHide', displayValue: 'Hide' },
    { value: 'labelShowIfUnread', displayValue: 'Show Label if Unread' },
  ] as const;
  const messageVisibiltyOptions = [
    { value: 'show', displayValue: 'Show' },
    { value: 'hide', displayValue: 'Hide' },
  ] as const;

  const newLabel = {
    name: labelName,
    messagesTotal: 0,
    messagesUnread: 0,
    threadsTotal: 0,
    threadsUnread: 0,
    messageVisibility: messageVisibility,
    labelVisibility: labelVisibility,
    type: 'user',
  } as LabelModel;

  const allLabelNames = userLabels.map((label) => {
    return label.name.toLocaleLowerCase();
  });

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setLabelName('');
        setlabelVisibilty('labelShow');
        setMessageVisibility('show');
        setHasError(false);
      }}
      title="Add Gmail Label"
      dialogContent={
        <Stack gap={2}>
          <TextField
            autoFocus
            id="labelName"
            error={hasError}
            helperText={hasError ? 'Label name is taken.' : ''}
            label="Label Name"
            placeholder="e.g. VIP Clients"
            fullWidth
            value={labelName}
            onChange={(event) => setLabelName(event.target.value)}
          />
          <Stack flexDirection="row" width="100%" gap={2}>
            <TextField
              label="Label Visibility"
              select
              fullWidth
              value={labelVisibility}
              onChange={(event) =>
                setlabelVisibilty(
                  event.target.value as LabelModel['labelVisibility']
                )
              }
            >
              {labelVisibiltyOptions.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.displayValue}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Message Visibility"
              value={messageVisibility}
              onChange={(event) =>
                setMessageVisibility(
                  event.target.value as LabelModel['messageVisibility']
                )
              }
            >
              {messageVisibiltyOptions.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.displayValue}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit,
        disabled: labelName === '',
        title: 'Add Label',
      }}
    />
  );
}
